/**
 * Scholar Parent Academy — Family Hub (passwordless magic-link dashboard).
 * /academy/family        -> request-a-link form (parent enters email)
 * /academy/family/:token -> live gamified dashboard for their scholar(s)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Flame, Trophy, Target, Award, CheckCircle2, Circle,
  CalendarDays, FileText, Mail, Star, GraduationCap, Sparkles,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { DailyQuote } from '../components/DailyQuote';
import { GpaHeadline, SubjectGrades, CategoryList } from '../components/gradebook/GradeSummary';

const ROYAL = '#2447D8';
const GOLD = '#C9A961';

const VERTEX_COLOR = {
  math: '#3B82F6', ela: '#8B5CF6', science: '#10B981',
  social_studies: '#F59E0B', sports: '#EF4444', consistency: '#06B6D4',
};

const RequestForm = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const request = async () => {
    if (!email) { toast.error('Enter your parent email'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/parent/link/request', { email });
      setSent(true);
      toast.success(r.data.message || 'Check your email');
    } catch (e) {
      toast.error('Something went wrong — please try again');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#08080a] text-white flex items-center justify-center px-4" data-testid="family-request">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src="/logos/saa-parent-mark.png" alt="Scholar Parent Academy" className="h-16 w-auto object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div className="border-2 border-zinc-800 rounded-2xl p-8 bg-[#0e0e12]">
          <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-2" style={{ color: ROYAL }}>Scholar Parent Academy</div>
          <h1 className="text-3xl font-black leading-tight mb-3">Your Scholar's<br />Progress — Live</h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Enter the parent email on file and we'll send a private link to your family dashboard — Scholar Card, daily quests, mastery, and materials. No password needed.
          </p>
          {sent ? (
            <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="family-request-sent">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-200">If that email is linked to an enrolled scholar, your dashboard link is on its way. Check your inbox.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                placeholder="Parent email" onKeyDown={(e) => e.key === 'Enter' && request()}
                className="w-full bg-black border-2 border-zinc-800 rounded-lg px-4 py-3 text-sm focus:border-blue-500 outline-none"
                data-testid="family-email-input"
              />
              <button onClick={request} disabled={busy}
                className="w-full text-white py-3.5 font-black text-xs tracking-[0.2em] uppercase rounded-lg flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ROYAL }} data-testid="family-request-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send My Dashboard Link</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ScholarCard = ({ card }) => {
  const initial = (card.name || 'S').charAt(0).toUpperCase();
  const pct = Math.round((card.xp_into_level / card.level_step) * 100);
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-zinc-800 bg-gradient-to-br from-[#0e0e14] to-[#141420]" data-testid="scholar-card">
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${ROYAL}, ${GOLD})` }} />
      <div className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-black shrink-0 overflow-hidden" style={{ backgroundColor: `${GOLD}22`, color: GOLD }}>
            {card.avatar_url ? <img src={card.avatar_url} alt={card.name} className="w-full h-full object-cover" /> : initial}
          </div>
          <div className="min-w-0">
            <div className="text-lg font-black text-white truncate">{card.name}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs mt-0.5">
              <span className="font-bold" style={{ color: GOLD }}>Grade {card.grade_level}</span>
              {card.sport && <><span className="text-zinc-600">·</span><span className="text-zinc-400">{card.sport}</span></>}
              {card.city && <><span className="text-zinc-600">·</span><span className="text-zinc-500">{card.city}</span></>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="text-center bg-black/40 rounded-xl py-3">
            <div className="text-2xl font-black" style={{ color: ROYAL }}>{card.overall_rating}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Scholar Rating</div>
          </div>
          <div className="text-center bg-black/40 rounded-xl py-3">
            <div className="text-2xl font-black text-orange-400 flex items-center justify-center gap-1"><Flame className="w-5 h-5" />{card.streak_days}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Day Streak</div>
          </div>
          <div className="text-center bg-black/40 rounded-xl py-3">
            <div className="text-2xl font-black text-white">Lv {card.level}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{card.xp} Scholar Dollars</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
            <span>Level {card.level}</span><span>{card.xp_to_next} SD to next level</span>
          </div>
          <div className="h-2 bg-black/50 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${ROYAL}, ${GOLD})` }} />
          </div>
        </div>

        {/* Billing status tile */}
        <BillingTile card={card} />
      </div>
    </div>
  );
};

const BillingTile = ({ card }) => {
  const suspended = card.billing_suspended;
  const tier = card.subscription_tier;
  const renews = card.subscription_renews_at;
  return (
    <div className={`mt-4 rounded-xl border-2 p-3 flex items-center justify-between gap-3 ${suspended ? 'border-red-500/60 bg-red-950/40' : 'border-zinc-800 bg-black/40'}`} data-testid="family-billing-tile">
      <div className="min-w-0">
        <div className={`text-[9px] uppercase tracking-[0.3em] font-black ${suspended ? 'text-red-400' : 'text-emerald-400'}`}>
          {suspended ? '⚠ Billing Paused' : 'Billing · Active'}
        </div>
        <div className="text-xs font-bold text-white truncate mt-0.5">
          {tier ? tier.replace(/_/g, ' ') : 'SAA Monthly Academic Block'}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          {suspended
            ? 'Your Scholar\u2019s dashboard is temporarily locked'
            : renews ? `Next renewal · ${new Date(renews).toLocaleDateString()}` : '$19.99 / month · renews automatically'}
        </div>
      </div>
      <a href={card.billing_invoice_url || '/academy/family'} target={card.billing_invoice_url ? '_blank' : '_self'} rel="noopener noreferrer"
         className={`shrink-0 inline-flex items-center px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${suspended ? 'bg-red-500 hover:bg-red-400 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}
         data-testid="family-billing-cta">
        {suspended ? 'Update Payment' : 'Manage'}
      </a>
    </div>
  );
};

const Panel = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-2xl border-2 border-zinc-800 bg-[#0e0e12] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4" style={{ color: GOLD }} />
      <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">{title}</h3>
    </div>
    {children}
  </div>
);

const ChildBoard = ({ child }) => (
  <div className="space-y-5" data-testid="child-board">
    <ScholarCard card={child.card} />

    {child.grades && (
      <Panel title="Grades & GPA" icon={GraduationCap} testid="panel-grades">
        <GpaHeadline summary={child.grades} />
        {child.grades.has_grades ? (
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            <SubjectGrades summary={child.grades} />
            <CategoryList categories={child.grades.categories} averages={Object.fromEntries(child.grades.categories.map((c) => [c.key, c.average]))} />
          </div>
        ) : (
          <div className="text-[11px] text-zinc-500 mt-3" data-testid="panel-grades-empty">No grades posted yet — they appear here as soon as the teacher enters scores.</div>
        )}
      </Panel>
    )}

    <div className="grid md:grid-cols-2 gap-5">
      <Panel title="Today's Quests" icon={Target} testid="panel-quests">
        <div className="space-y-2.5">
          {child.quests.map((q) => (
            <div key={q.key} className="flex items-center gap-3">
              {q.done ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <Circle className="w-5 h-5 text-zinc-600 shrink-0" />}
              <span className={`text-sm flex-1 ${q.done ? 'text-white' : 'text-zinc-500'}`}>{q.label}</span>
              <span className="text-[10px] font-bold" style={{ color: q.done ? GOLD : '#52525b' }}>+{q.xp}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Mastery Radar" icon={Sparkles} testid="panel-mastery">
        <div className="space-y-2.5">
          {child.mastery.vertices.map((v) => (
            <div key={v.key}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-zinc-400 truncate pr-2">{v.label}</span>
                <span className="font-bold text-white">{v.score}</span>
              </div>
              <div className="h-1.5 bg-black/50 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${v.score}%`, backgroundColor: VERTEX_COLOR[v.key] || ROYAL }} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>

    {child.combine && child.combine.total_sets > 0 && (
      <Panel title="Combine Reps" icon={Flame} testid="panel-combine">
        <div className="flex items-center gap-4 mb-4">
          <div className="text-center bg-black/40 rounded-xl px-4 py-2.5">
            <div className="text-2xl font-black" style={{ color: GOLD }}>{child.combine.total_sets}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500">Sets Run</div>
          </div>
          <div className="text-center bg-black/40 rounded-xl px-4 py-2.5">
            <div className="text-2xl font-black text-orange-400 flex items-center justify-center gap-1"><Flame className="w-5 h-5" />{child.combine.best_streak}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500">Best Streak</div>
          </div>
        </div>
        <div className="space-y-2.5">
          {child.combine.averages.map((a) => (
            <div key={a.subject}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-zinc-400">{a.title} <span className="text-zinc-600">· {a.sets} sets</span></span>
                <span className="font-bold text-white">{a.avg}%</span>
              </div>
              <div className="h-1.5 bg-black/50 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${a.avg}%`, backgroundColor: ROYAL }} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    )}

    <Panel title="Trophy Case" icon={Trophy} testid="panel-badges">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {child.badges.map((b) => (
          <div key={b.key} className={`rounded-xl p-3 text-center border ${b.earned ? 'border-amber-500/40 bg-amber-500/5' : 'border-zinc-800 bg-black/30 opacity-50'}`} data-testid={`badge-${b.key}`}>
            <Award className={`w-6 h-6 mx-auto mb-1.5 ${b.earned ? 'text-amber-400' : 'text-zinc-600'}`} />
            <div className={`text-[10px] font-bold leading-tight ${b.earned ? 'text-white' : 'text-zinc-500'}`}>{b.name}</div>
          </div>
        ))}
      </div>
    </Panel>

    <Panel title="Week at a Glance" icon={CalendarDays} testid="panel-week">
      <div className="grid grid-cols-5 gap-2">
        {child.week.map((d) => (
          <div key={d.date} className={`rounded-xl p-3 text-center border ${d.is_today ? 'border-blue-500/50 bg-blue-500/10' : 'border-zinc-800 bg-black/30'}`}>
            <div className="text-[10px] uppercase tracking-wider font-black" style={{ color: d.is_today ? ROYAL : '#71717a' }}>{d.weekday}</div>
            <div className="text-[11px] text-white font-bold mt-1.5 leading-tight">{(d.subject || '').split(' ')[0]}</div>
            {d.standard_code && <div className="text-[9px] text-zinc-500 mt-1 font-mono">{d.standard_code}</div>}
          </div>
        ))}
      </div>
    </Panel>

    <Panel title="Materials from Coach" icon={FileText} testid="panel-materials">
      {child.materials.length === 0 ? (
        <p className="text-sm text-zinc-500">No materials sent yet. When your scholar's coach shares a resource, it'll appear here.</p>
      ) : (
        <div className="space-y-2">
          {child.materials.map((m, i) => (
            <div key={i} className="flex items-center gap-3 bg-black/30 rounded-lg p-3 border border-zinc-800">
              <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{m.title}</div>
                {m.note && <div className="text-xs text-zinc-500 truncate">{m.note}</div>}
              </div>
              {!m.read && <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded" style={{ backgroundColor: `${ROYAL}30`, color: '#93b4ff' }}>New</span>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  </div>
);

const FamilyHub = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await api.get(`/academy/parent/link/${encodeURIComponent(token)}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'This link is invalid or has expired.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <RequestForm />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: ROYAL }} />
          <p className="text-zinc-500 uppercase tracking-[0.3em] text-xs">Loading Family Hub</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#08080a] flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="family-error">
          <GraduationCap className="w-12 h-12 mx-auto mb-4" style={{ color: ROYAL }} />
          <h2 className="text-xl font-black text-white mb-2">Link Not Available</h2>
          <p className="text-zinc-400 text-sm mb-5">{error}</p>
          <a href="/academy/family" className="inline-block text-white px-5 py-3 rounded-lg font-black text-xs uppercase tracking-wider" style={{ backgroundColor: ROYAL }} data-testid="family-request-new">
            Request a New Link
          </a>
        </div>
      </div>
    );
  }

  const children = data?.children || [];
  const child = children[active];

  return (
    <div className="min-h-screen bg-[#08080a] pb-16" data-testid="family-hub">
      <div className="border-b border-zinc-900 sticky top-0 z-10 bg-[#08080a]/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logos/saa-parent-mark.png" alt="Scholar Parent Academy" className="h-11 w-auto object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <div className="text-white font-black text-base leading-tight">Family Hub</div>
            <div className="text-[11px] text-zinc-500">{data.parent_name ? `${data.parent_name} · ` : ''}Scholar Parent Academy</div>
          </div>
        </div>
        {children.length > 1 && (
          <div className="max-w-4xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto">
            {children.map((c, i) => (
              <button key={i} onClick={() => setActive(i)}
                className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${i === active ? 'text-white' : 'bg-zinc-900 text-zinc-400'}`}
                style={i === active ? { backgroundColor: ROYAL } : {}}
                data-testid={`child-tab-${i}`}>
                {c.card.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-6">
        {/* Daily rotating motivation for parents */}
        <DailyQuote audience="parent" variant="dark" className="mb-5" />
        {child ? <ChildBoard child={child} /> : (
          <div className="text-center text-zinc-500 py-20"><Star className="w-10 h-10 mx-auto mb-3" style={{ color: GOLD }} />No scholar data yet.</div>
        )}
      </div>
    </div>
  );
};

export default FamilyHub;
